/**
 * Domain Event — the fundamental communication unit between autonomous services.
 * Published to EventBridge (Event_Hub) on every state change.
 * All events are immutable facts about something that happened.
 */
export interface DomainEvent {
  /** Unique event identifier (UUID v4) */
  readonly eventId: string;
  /** Event type in PascalCase (e.g., "CardCreated", "BoardArchived") */
  readonly eventType: string;
  /** When the event occurred (ISO 8601 UTC) */
  readonly timestamp: string;
  /** Service that produced this event (e.g., "CardService") */
  readonly sourceService: string;
  /** User ID who triggered the action */
  readonly actorId: string;
  /** Primary entity ID affected */
  readonly aggregateId: string;
  /** Entity type (e.g., "Card", "Board", "List") */
  readonly aggregateType: string;
  /** Monotonically increasing version for conflict resolution */
  readonly aggregateVersion: number;
  /** Correlation ID for tracing a request through the system */
  readonly correlationId: string;
  /** Event-specific payload data */
  readonly payload: Record<string, unknown>;
}

/**
 * Validates that a string is a UUID v4 format.
 */
export function isValidUuidV4(value: string): boolean {
  const UUID_V4_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return UUID_V4_REGEX.test(value);
}

/**
 * Validates that a string is PascalCase (starts with uppercase, no spaces/special chars).
 */
export function isPascalCase(value: string): boolean {
  const PASCAL_CASE_REGEX = /^[A-Z][a-zA-Z0-9]*$/;
  return PASCAL_CASE_REGEX.test(value);
}

/**
 * Validates that a string is a valid ISO 8601 UTC timestamp.
 */
export function isValidIso8601Utc(value: string): boolean {
  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return false;
  }
  // Must end with Z (UTC)
  return value.endsWith('Z');
}

/**
 * Validates a DomainEvent against the schema.
 * Returns an array of error messages (empty if valid).
 */
export function validateDomainEvent(event: unknown): string[] {
  const errors: string[] = [];

  if (typeof event !== 'object' || event === null) {
    return ['Event must be a non-null object'];
  }

  const e = event as Record<string, unknown>;

  if (typeof e['eventId'] !== 'string' || !isValidUuidV4(e['eventId'])) {
    errors.push('eventId must be a valid UUID v4');
  }

  if (typeof e['eventType'] !== 'string' || !isPascalCase(e['eventType'])) {
    errors.push('eventType must be a non-empty PascalCase string');
  }

  if (typeof e['timestamp'] !== 'string' || !isValidIso8601Utc(e['timestamp'])) {
    errors.push('timestamp must be a valid ISO 8601 UTC string ending with Z');
  }

  if (typeof e['sourceService'] !== 'string' || e['sourceService'].length === 0) {
    errors.push('sourceService must be a non-empty string');
  }

  if (typeof e['actorId'] !== 'string' || e['actorId'].length === 0) {
    errors.push('actorId must be a non-empty string');
  }

  if (typeof e['aggregateId'] !== 'string' || e['aggregateId'].length === 0) {
    errors.push('aggregateId must be a non-empty string');
  }

  if (typeof e['aggregateType'] !== 'string' || e['aggregateType'].length === 0) {
    errors.push('aggregateType must be a non-empty string');
  }

  if (typeof e['aggregateVersion'] !== 'number' || !Number.isInteger(e['aggregateVersion']) || e['aggregateVersion'] < 1) {
    errors.push('aggregateVersion must be a positive integer');
  }

  if (typeof e['correlationId'] !== 'string' || e['correlationId'].length === 0) {
    errors.push('correlationId must be a non-empty string');
  }

  if (typeof e['payload'] !== 'object' || e['payload'] === null || Array.isArray(e['payload'])) {
    errors.push('payload must be a non-null object');
  }

  return errors;
}
